import { baseApi } from 'core/api/base'
import {
  IApplication,
  IApplicationPromptVariable,
  McpApplicationListItem,
  ResponsablesMode,
} from 'core/interfaces/Application'
import { IIconeService } from 'core/interfaces/Service'
import { generatePath } from 'react-router'

const CLIENT_APPLICATIONS_ENDPOINT = '/clients/:idClient/applications'
const CLIENT_MCP_APPLICATIONS_ENDPOINT = '/clients/:idClient/mcp-applications'

export const APPLICATIONS_ENDPOINT = '/applications'
export const APPLICATION_ENDPOINT = `${APPLICATIONS_ENDPOINT}/:applicationId`
export const PUBLISH_APPLICATION_ENDPOINT = `${APPLICATION_ENDPOINT}/publier`
export const APPLICATION_PROMPT_VARIABLE_ENDPOINT = `/clients/:idClient/applications/:applicationName/prompt-variables`

const applicationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllApplicationsByClientFilterByTags: builder.query<
      IApplication[],
      {
        idClient: number
        tags?: number[]
        services?: number[]
      }
    >({
      query: ({ idClient, tags = [], services = [] }) => {
        const params = new URLSearchParams()

        tags.forEach((tag) => {
          params.append('tagIds[]', tag.toString())
        })

        services.forEach((service) => {
          params.append('serviceIds[]', service.toString())
        })

        const queryString = params.toString()
        return `${generatePath(CLIENT_APPLICATIONS_ENDPOINT, { idClient: idClient.toString() })}${queryString !== '' ? '?' + queryString : ''}`
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Application', id }) as const),
              { type: 'Application', id: 'LIST' },
            ]
          : [{ type: 'Application', id: 'LIST' }],
    }),
    getAllApplicationsByClient: builder.query<
      IApplication[],
      {
        idClient: number
        published?: boolean
      }
    >({
      query: ({ idClient, published }) => {
        const url = generatePath(CLIENT_APPLICATIONS_ENDPOINT, {
          idClient: idClient.toString(),
        })

        const searchParams = new URLSearchParams()
        if (published !== undefined) {
          searchParams.append('publiee', String(published))
        }

        return {
          url: `${url}?${searchParams.toString()}`,
          method: 'GET',
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Application', id }) as const),
              { type: 'Application', id: 'LIST' },
            ]
          : [{ type: 'Application', id: 'LIST' }],
    }),
    getApplication: builder.query<IApplication, { applicationId: number }>({
      query: ({ applicationId }) =>
        generatePath(APPLICATION_ENDPOINT, {
          applicationId: applicationId.toString(),
        }),
      providesTags: (result, error, { applicationId }) => [
        { type: 'Application', applicationId },
      ],
    }),
    getMcpApplicationsByClient: builder.query<
      McpApplicationListItem[],
      { idClient: number }
    >({
      query: ({ idClient }) =>
        generatePath(CLIENT_MCP_APPLICATIONS_ENDPOINT, {
          idClient: idClient.toString(),
        }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Application', id }) as const),
              { type: 'Application', id: 'MCP_LIST' },
            ]
          : [{ type: 'Application', id: 'MCP_LIST' }],
    }),
    createApplication: builder.mutation<
      IApplication,
      {
        idClient: number
        idService: number
        nomApplication: string
        nomTechnique: string
        tags?: number[]
        description: string
        accroche: string
        icone?: IIconeService | null
        responsablesMode: ResponsablesMode
        responsablesUtilisateurIds?: number[]
        accessibleMcp?: boolean
        mcpUtilisateurIds?: number[]
      }
    >({
      query: ({
        idClient,
        idService,
        nomApplication,
        nomTechnique,
        tags,
        description,
        accroche,
        icone,
        responsablesMode,
        responsablesUtilisateurIds,
        accessibleMcp,
        mcpUtilisateurIds,
      }) => ({
        url: generatePath(CLIENT_APPLICATIONS_ENDPOINT, {
          idClient: idClient.toString(),
        }),
        method: 'POST',
        body: {
          idService,
          nomApplication,
          nomTechnique,
          tags,
          description,
          accroche,
          icone,
          responsablesMode,
          responsablesUtilisateurIds,
          accessibleMcp,
          mcpUtilisateurIds,
        },
      }),
      invalidatesTags: [{ type: 'Application', id: 'LIST' }],
    }),
    updateApplication: builder.mutation<
      IApplication,
      {
        idApplication: number
        idService: number
        nomApplication: string
        tags?: number[]
        description: string
        accroche: string
        icone?: IIconeService | null
        responsablesMode?: ResponsablesMode
        responsablesUtilisateurIds?: number[]
        accessibleMcp?: boolean
        mcpUtilisateurIds?: number[]
      }
    >({
      query: ({
        idApplication,
        idService,
        nomApplication,
        tags,
        description,
        accroche,
        icone,
        responsablesMode,
        responsablesUtilisateurIds,
        accessibleMcp,
        mcpUtilisateurIds,
      }) => {
        const body: {
          idService: number
          nomApplication: string
          tags?: number[]
          description: string
          accroche: string
          icone?: IIconeService | null
          responsablesMode?: ResponsablesMode
          responsablesUtilisateurIds?: number[]
          accessibleMcp?: boolean
          mcpUtilisateurIds?: number[]
        } = {
          idService,
          nomApplication,
          tags,
          description,
          accroche,
          icone,
          accessibleMcp,
          mcpUtilisateurIds,
        }

        if (responsablesMode !== undefined) {
          body.responsablesMode = responsablesMode

          if (
            responsablesMode === 'selected_users' &&
            responsablesUtilisateurIds !== undefined
          ) {
            body.responsablesUtilisateurIds = responsablesUtilisateurIds
          }
        }

        return {
          url: generatePath(APPLICATION_ENDPOINT, {
            applicationId: idApplication.toString(),
          }),
          method: 'PATCH',
          body,
        }
      },
      invalidatesTags: (result, error, { idApplication }) => [
        { type: 'Application', idApplication },
      ],
    }),
    getApplicationPromptVariablesByClient: builder.query<
      IApplicationPromptVariable,
      {
        idClient: number
        applicationName: string
      }
    >({
      query: ({ idClient, applicationName }) =>
        generatePath(APPLICATION_PROMPT_VARIABLE_ENDPOINT, {
          idClient: idClient.toString(),
          applicationName,
        }),
    }),
    publishApplication: builder.mutation<
      IApplication,
      {
        idApplication: number
        publiee: boolean
      }
    >({
      query({ idApplication, publiee }) {
        return {
          url: generatePath(PUBLISH_APPLICATION_ENDPOINT, {
            applicationId: idApplication.toString(),
          }),
          method: 'PATCH',
          body: { publiee },
        }
      },
      invalidatesTags: (result, error, { idApplication }) => [
        { type: 'Application', idApplication },
      ],
    }),
  }),
})

export const {
  useGetAllApplicationsByClientFilterByTagsQuery,
  useGetAllApplicationsByClientQuery,
  useGetApplicationQuery,
  useGetMcpApplicationsByClientQuery,
  useCreateApplicationMutation,
  useUpdateApplicationMutation,
  useGetApplicationPromptVariablesByClientQuery,
  usePublishApplicationMutation,
} = applicationApi
export { applicationApi }
